import type { AuditLogEntry } from '@condo/contracts';
import { Hash, ShieldCheck } from 'lucide-react';
import { formatLongDate, formatRelative } from '@/lib/format/date';

const EVENT_COLORS: Record<string, string> = {
  'condominio.created': 'var(--color-primary)',
  'gasto_comun.emitido': 'var(--color-accent)',
  'pago.iniciado': 'var(--color-ochre)',
  'pago.reconciliado': 'var(--color-moss)',
  'pago.rechazado': 'var(--color-danger)',
  'residente.created': 'var(--color-primary)',
  'asamblea.convocada': 'var(--color-primary)',
  'acta.generated': 'var(--color-moss)',
};

function colorFor(eventType: string): string {
  return EVENT_COLORS[eventType] ?? 'var(--color-ink-muted)';
}

export function AuditTimeline({ entries }: { entries: AuditLogEntry[] }) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-[var(--color-ink-muted)]">
        Sin actividad registrada todavia.
      </p>
    );
  }
  return (
    <ol className="relative ml-4 border-l border-[var(--color-hairline-strong)]">
      {entries.map((e) => {
        const color = colorFor(e.eventType);
        return (
          <li key={e.id} className="relative pb-8 pl-6">
            <span
              aria-hidden
              className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-sm border-2 border-[var(--color-paper)]"
              style={{ background: color }}
            />
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h3 className="font-display text-sm font-semibold text-[var(--color-ink)]">
                {e.eventType.replaceAll('.', ' · ').replaceAll('_', ' ')}
              </h3>
              <span className="text-[0.6875rem] uppercase tracking-[0.1em] text-[var(--color-ink-faint)]">
                {e.entityType}
              </span>
            </div>
            <p className="mt-1 text-xs text-[var(--color-ink-muted)]">
              {formatLongDate(e.timestamp)} · {formatRelative(e.timestamp)}
            </p>
            <p className="mt-1 text-[0.6875rem] text-[var(--color-ink-muted)]">
              Actor: <span className="font-mono">{e.actor.rol}</span> /{' '}
              <span className="font-mono">{e.actor.userId}</span>
            </p>
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-[var(--color-paper-dim)] px-2 py-1 font-mono text-[0.625rem] text-[var(--color-ink-muted)]">
              <Hash className="h-3 w-3" />
              {e.hashCurrent.slice(0, 16)}...
              {e.hashPrev && (
                <>
                  <span className="mx-1 opacity-50">←</span>
                  {e.hashPrev.slice(0, 8)}...
                </>
              )}
              <ShieldCheck className="ml-1 h-3 w-3 text-[var(--color-moss)]" />
            </div>
          </li>
        );
      })}
    </ol>
  );
}
